import { Page, Text, View, Document, StyleSheet, Image } from "@react-pdf/renderer";
import printlogo from "../../../printpropertise/printlogoceylonika.png";

// Helper to remove HTML tags from adjustment notes
const stripHtml = (html) => html.replace(/<[^>]*>?/gm, "");

// --- Styles ---
const pdfStyles = StyleSheet.create({
  page: {
    paddingTop: 35,
    paddingBottom: 65,
    paddingHorizontal: 35,
    fontSize: 9,
    fontFamily: "Helvetica",
  },
  // --- Header ---
  headerSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#cccccc",
    paddingBottom: 10,
  },
  companyDetails: {
    flexDirection: "column",
  },
  logo: {
    width: 150,
    height: 45,
    marginBottom: 5,
  },
  companyName: {
    fontSize: 11,
    fontFamily: "Helvetica-Bold",
  },
  companyAddress: {
    fontSize: 9,
    maxWidth: 250,
  },
  reportInfo: {
    textAlign: "right",
    justifyContent: "flex-end",
  },
  reportTitle: {
    fontSize: 14,
    fontFamily: "Helvetica-Bold",
    marginBottom: 5,
  },
  filterInfo: {
    fontSize: 9,
    fontFamily: "Helvetica-Oblique",
    marginBottom: 2,
  },

  // --- Section & Table General ---
  sectionTitle: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
    backgroundColor: "#e0e0e0",
    padding: 5,
    marginBottom: 10,
    marginTop: 15,
  },
  table: {
    display: "table",
    width: "auto",
    borderStyle: "solid",
    borderColor: "#bfbfbf",
    borderWidth: 1,
    borderRightWidth: 0,
    borderBottomWidth: 0,
  },
  tableRow: {
    flexDirection: "row",
  },
  tableColHeader: {
    borderStyle: "solid",
    borderColor: "#bfbfbf",
    borderBottomColor: "#000",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    backgroundColor: "#e0e0e0",
    padding: 4,
    textAlign: "center",
    fontFamily: "Helvetica-Bold",
    fontSize: 8,
  },
  tableCol: {
    borderStyle: "solid",
    borderColor: "#bfbfbf",
    borderWidth: 1,
    borderLeftWidth: 0,
    borderTopWidth: 0,
    padding: 3,
  },
  tableCell: {
    fontSize: 8,
    wordWrap: "break-word",
  },
  tableCellRight: {
    textAlign: "right",
    fontSize: 8,
  },

  // --- Column Widths ---
  grnCol1: { width: "15%" },
  grnCol2: { width: "30%" },
  grnCol3: { width: "15%" },
  grnCol4: { width: "20%" },
  grnCol5: { width: "20%" },
  transCol1: { width: "20%" },
  transCol2: { width: "30%" },
  transCol3: { width: "30%" },
  transCol4: { width: "20%" },
  adjCol1: { width: "20%" },
  adjCol2: { width: "15%" },
  adjCol3: { width: "10%" },
  adjCol4: { width: "10%" },
  adjCol5: { width: "45%" },
  ordCol1: { width: "40%" },
  ordCol2: { width: "30%" },
  ordCol3: { width: "30%" },

  // --- Summary Section ---
  summaryContainer: {
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    marginBottom: 10,
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderStyle: "solid", // ✅ fixed
    padding: 5,
  },
  summaryItem: {
    width: "33%",
    padding: 5,
  },
  summaryLabel: {
    fontSize: 8,
    fontFamily: "Helvetica-Bold",
    color: "#444",
  },
  summaryValue: {
    fontSize: 12,
    fontFamily: "Helvetica-Bold",
  },

  // --- Footer ---
  footer: {
    position: "absolute",
    bottom: 30,
    left: 35,
    right: 35,
    textAlign: "center",
    color: "grey",
    fontSize: 8,
  },
});

// --- PDF Document Component ---
export const StockMovementPdfDocument = ({ data, filters, productName }) => {
  const { year, month } = filters;
  const monthName = new Date(year, month - 1, 1).toLocaleString("default", {
    month: "long",
  });

  return (
    <Document>
      <Page size="A4" style={pdfStyles.page} orientation="portrait">
        {/* --- Header --- */}
        <View style={pdfStyles.headerSection}>
          <View style={pdfStyles.companyDetails}>
            <Image src={printlogo} style={pdfStyles.logo} />
            <Text style={pdfStyles.companyName}>Ceylonika Cosmetics (Pvt) Ltd</Text>
            <Text style={pdfStyles.companyAddress}>
              154/11, Pushparama Road, Kadawatha
            </Text>
          </View>
          <View style={pdfStyles.reportInfo}>
            <Text style={pdfStyles.reportTitle}>Stock Item Movement</Text>
            <Text style={pdfStyles.filterInfo}>Product: {productName}</Text>
            <Text style={pdfStyles.filterInfo}>
              Period: {monthName} {year}
            </Text>
            <Text style={pdfStyles.filterInfo}>
              Printed: {new Date().toLocaleString()}
            </Text>
          </View>
        </View>

        {/* --- Summary --- */}
        <View style={pdfStyles.summaryContainer}>
          <View style={pdfStyles.summaryItem}>
            <Text style={pdfStyles.summaryLabel}>Total GRN Qty</Text>
            <Text style={pdfStyles.summaryValue}>{data.sumGrnQty}</Text>
          </View>
          <View style={pdfStyles.summaryItem}>
            <Text style={pdfStyles.summaryLabel}>Total Transfer Qty</Text>
            <Text style={pdfStyles.summaryValue}>{data.sumTransQty}</Text>
          </View>
          <View style={pdfStyles.summaryItem}>
            <Text style={pdfStyles.summaryLabel}>Total Order Qty</Text>
            <Text style={pdfStyles.summaryValue}>{data.sumOrderQty}</Text>
          </View>
          <View style={pdfStyles.summaryItem}>
            <Text style={pdfStyles.summaryLabel}>Adjustments (Add)</Text>
            <Text style={pdfStyles.summaryValue}>{data.sumAdjustAddQty}</Text>
          </View>
          <View style={pdfStyles.summaryItem}>
            <Text style={pdfStyles.summaryLabel}>Adjustments (Minus)</Text>
            <Text style={pdfStyles.summaryValue}>{data.sumAdjustMinQty}</Text>
          </View>
        </View>

        {/* --- GRN Table --- */}
        {data.grnList?.length > 0 && (
          <View>
            <Text style={pdfStyles.sectionTitle}>GRN Details</Text>
            <View style={pdfStyles.table}>
              <View style={pdfStyles.tableRow} fixed>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.grnCol1]}>GRN Ref</Text>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.grnCol2]}>Stock</Text>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.grnCol3]}>Qty</Text>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.grnCol4]}>Cost</Text>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.grnCol5]}>Total</Text>
              </View>
              {data.grnList.map((item) => (
                <View style={pdfStyles.tableRow} key={item.grN_REF} wrap={false}>
                  <View style={[pdfStyles.tableCol, pdfStyles.grnCol1]}>
                    <Text style={pdfStyles.tableCell}>{item.grN_REF}</Text>
                  </View>
                  <View style={[pdfStyles.tableCol, pdfStyles.grnCol2]}>
                    <Text style={pdfStyles.tableCell}>{item.stocK_NAME}</Text>
                  </View>
                  <View style={[pdfStyles.tableCol, pdfStyles.grnCol3]}>
                    <Text style={pdfStyles.tableCellRight}>{item.grN_QTY}</Text>
                  </View>
                  <View style={[pdfStyles.tableCol, pdfStyles.grnCol4]}>
                    <Text style={pdfStyles.tableCellRight}>
                      {item.grN_COST.toFixed(2)}
                    </Text>
                  </View>
                  <View style={[pdfStyles.tableCol, pdfStyles.grnCol5]}>
                    <Text style={pdfStyles.tableCellRight}>
                      {item.grN_TOTAL.toFixed(2)}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* --- Transfer Table --- */}
        {data.transList?.length > 0 && (
          <View>
            <Text style={pdfStyles.sectionTitle}>Transfer Details</Text>
            <View style={pdfStyles.table}>
              <View style={pdfStyles.tableRow} fixed>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.transCol1]}>
                  Transfer Ref
                </Text>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.transCol2]}>
                  From Stock
                </Text>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.transCol3]}>
                  To Stock
                </Text>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.transCol4]}>Qty</Text>
              </View>
              {data.transList.map((item) => (
                <View style={pdfStyles.tableRow} key={item.tranS_REF} wrap={false}>
                  <View style={[pdfStyles.tableCol, pdfStyles.transCol1]}>
                    <Text style={pdfStyles.tableCell}>{item.tranS_REF}</Text>
                  </View>
                  <View style={[pdfStyles.tableCol, pdfStyles.transCol2]}>
                    <Text style={pdfStyles.tableCell}>{item.fromstock}</Text>
                  </View>
                  <View style={[pdfStyles.tableCol, pdfStyles.transCol3]}>
                    <Text style={pdfStyles.tableCell}>{item.tostock}</Text>
                  </View>
                  <View style={[pdfStyles.tableCol, pdfStyles.transCol4]}>
                    <Text style={pdfStyles.tableCellRight}>{item.tranS_QTY}</Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* --- Adjustment Table --- */}
        {data.adjustList?.length > 0 && (
          <View>
            <Text style={pdfStyles.sectionTitle}>Adjustment Details</Text>
            <View style={pdfStyles.table}>
              <View style={pdfStyles.tableRow} fixed>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.adjCol1]}>Date</Text>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.adjCol2]}>Stock</Text>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.adjCol3]}>Type</Text>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.adjCol4]}>Qty</Text>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.adjCol5]}>Note</Text>
              </View>
              {data.adjustList.map((item, index) => (
                <View
                  style={pdfStyles.tableRow}
                  key={`${item.cR_DT}-${index}`}
                  wrap={false}
                >
                  <View style={[pdfStyles.tableCol, pdfStyles.adjCol1]}>
                    <Text style={pdfStyles.tableCell}>{item.cR_DT}</Text>
                  </View>
                  <View style={[pdfStyles.tableCol, pdfStyles.adjCol2]}>
                    <Text style={pdfStyles.tableCell}>{item.stocK_NAME}</Text>
                  </View>
                  <View style={[pdfStyles.tableCol, pdfStyles.adjCol3]}>
                    <Text style={pdfStyles.tableCell}>{item.adjusT_TYPE}</Text>
                  </View>
                  <View style={[pdfStyles.tableCol, pdfStyles.adjCol4]}>
                    <Text style={pdfStyles.tableCellRight}>{item.adjusT_QTY}</Text>
                  </View>
                  <View style={[pdfStyles.tableCol, pdfStyles.adjCol5]}>
                    <Text style={pdfStyles.tableCell}>
                      {stripHtml(item.adjusT_NOTE)}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* --- Order Table --- */}
        {data.orderList?.length > 0 && (
          <View>
            <Text style={pdfStyles.sectionTitle}>Order Details</Text>
            <View style={pdfStyles.table}>
              <View style={pdfStyles.tableRow} fixed>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.ordCol1]}>
                  Order Ref
                </Text>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.ordCol2]}>Qty</Text>
                <Text style={[pdfStyles.tableColHeader, pdfStyles.ordCol3]}>
                  Subtotal
                </Text>
              </View>
              {data.orderList.map((item) => (
                <View style={pdfStyles.tableRow} key={item.ordeR_REF} wrap={false}>
                  <View style={[pdfStyles.tableCol, pdfStyles.ordCol1]}>
                    <Text style={pdfStyles.tableCell}>{item.ordeR_REF}</Text>
                  </View>
                  <View style={[pdfStyles.tableCol, pdfStyles.ordCol2]}>
                    <Text style={pdfStyles.tableCellRight}>{item.ordeR_QTY}</Text>
                  </View>
                  <View style={[pdfStyles.tableCol, pdfStyles.ordCol3]}>
                    <Text style={pdfStyles.tableCellRight}>
                      {item.ordeR_SUBTOTAL.toFixed(2)}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          </View>
        )}

        {/* --- Footer --- */}
        <Text
          style={pdfStyles.footer}
          render={({ pageNumber, totalPages }) =>
            `Page ${pageNumber} of ${totalPages}`
          }
          fixed
        />
      </Page>
    </Document>
  );
};
